import React from 'react';
import { Loader2 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { PaymentMethodBreakdown } from '@/types/savingsCalculator';

interface PaymentMethodsChartProps {
  data: PaymentMethodBreakdown[];
  loading?: boolean;
}

const COLORS: Record<string, string> = {
  Card: '#3b82f6',   // blue-500
  ACH: '#34d399',    // emerald-400
  Other: '#d1d5db',  // gray-300
};

const PaymentMethodsChart: React.FC<PaymentMethodsChartProps> = ({ data, loading = false }) => {
  return (
    <div className="border-2 border-muted rounded-xl bg-white dark:bg-background p-6">
      <h3 className="text-base text-muted-foreground mb-4">Payment Methods</h3>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : data.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No transaction data available</p>
      ) : (
        <div className="flex items-center gap-6">
          {/* Legend */}
          <div className="flex flex-col gap-2 min-w-[120px]">
            {data.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: COLORS[item.name] || COLORS.Other }}
                />
                <span className="text-sm text-foreground font-medium">
                  {item.name}: {item.percentage}%
                </span>
              </div>
            ))}
          </div>

          <div className="w-[160px] h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={25}
                  outerRadius={75}
                  strokeWidth={2}
                  stroke="#fff"
                >
                  {data.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={COLORS[entry.name] || COLORS.Other}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const item = payload[0].payload as PaymentMethodBreakdown;
                    return (
                      <div className="bg-white dark:bg-background border border-border rounded-lg shadow-md px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: COLORS[item.name] || COLORS.Other }}
                          />
                          <span className="font-semibold text-foreground">{item.name}</span>
                        </div>
                        <p className="text-muted-foreground">
                          {item.count.toLocaleString()} transaction{item.count !== 1 ? 's' : ''}
                        </p>
                        <p className="text-muted-foreground">{item.percentage}% of total</p>
                      </div>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsChart;
